// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md
// Bulk import — the classifier. Turns a dropped documentation folder into a
// proposed manifest with zero typing: brand/product from the folder path,
// document type from folder or filename vocabulary, language from locale
// tokens in the file name or a language folder, duplicates grouped by content
// hash where the wizard has one (else by size + locale-stripped name). Manual
// include/exclude choices are passed back in so re-classifying keeps them.
// Pure module — unit-testable without the DOM; the wizard owns React state.

export interface ScanFile { file: File; relPath: string }

export type DocKind = 'installation' | 'operation' | 'quickstart' | 'service' | 'compliance' | 'template' | 'other';

export interface Proposal {
  /** stable key: the relative path */
  key: string;
  file: File;
  relPath: string;
  brand: string;
  product: string;
  kind: DocKind;
  kindLabel: string;
  /** catalog type key to create the document with */
  typeKey: string;
  title: string;
  language: string;          // 'EN', 'FR', … or 'EN?' when no token found
  english: boolean;
  sizeBytes: number;
  oversize: boolean;
  /** set on non-primary members of a duplicate group (same bytes, several models) */
  dupOf: string | null;
  /** on the primary of a duplicate group: every product the file applies to */
  mergedProducts: string[];
  include: boolean;
  excludeReason: 'translation' | 'compliance' | 'oversize' | 'duplicate' | 'manual' | null;
}

export interface ScanStats {
  totalFiles: number;
  docFiles: number;
  ignoredOther: number;      // software, CAD, zips…
  oversize: number;
  translations: number;
  duplicates: number;
  products: string[];
  kinds: DocKind[];
  sidecarCount: number;
}

export const MAX_FILE_BYTES = 95 * 1024 * 1024;
const DOC_EXTS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic']);

// First match wins, so the order matters: quick starts come before Operation
// ("Basic Operation Guide", "Quick Reference" are quick starts, not manuals).
const KIND_RULES: Array<{ re: RegExp; kind: DocKind; label: string }> = [
  { re: /installation|install guide|mounting instructions/i, kind: 'installation', label: 'Installation' },
  { re: /quick ?start|quick ?reference|quick ?guide|getting started|basic operation/i, kind: 'quickstart', label: 'Quick start' },
  { re: /operation|operator|user guide|user manual|reference|owner'?s handbook|handbook/i, kind: 'operation', label: 'Operation' },
  { re: /calibration|commissioning|service|maintenance/i, kind: 'service', label: 'Service' },
  { re: /\bdoc\b|declaration|conformity|compliance|certificate|srrc|ukca|rcm|psti|disclaimer|safety (?:information|instructions|notice|warnings?)/i, kind: 'compliance', label: 'Compliance' },
  { re: /mounting template|template|drilling/i, kind: 'template', label: 'Template' },
];

const LOCALE_BRACKET = /\[([a-z]{2})-([a-z]{2})\]/i;
const LOCALE_PAREN = /\((EN|FR|DE|ES|IT|NL|SV|NB|DA|FI|PT|RU|ZH|JA|KO|PL|CS|EL|HE|TR|AR|HR|SL|ET|LV|LT|BG|RO|HU|SK|MT|GA|PT-BR|ZH-CN|ZH-TW|NL-NL|EN-US|EN-GB)\)/i;

/** Two-letter codes read from a trailing `_FR` / `-de` / `_en-GB` token or a folder name. */
const LANG_CODES = new Set(['EN', 'FR', 'DE', 'ES', 'IT', 'NL', 'SV', 'NB', 'DA', 'FI', 'PT', 'RU', 'ZH', 'JA', 'KO', 'PL', 'CS', 'EL', 'TR', 'AR', 'HR', 'SL', 'BG', 'RO', 'HU', 'SK', 'LT', 'LV', 'ET']);
const LANG_NAMES: Record<string, string> = {
  english: 'EN', french: 'FR', francais: 'FR', 'français': 'FR', german: 'DE', deutsch: 'DE', spanish: 'ES', espanol: 'ES', 'español': 'ES',
  italian: 'IT', italiano: 'IT', dutch: 'NL', nederlands: 'NL', swedish: 'SV', svenska: 'SV', norwegian: 'NB', norsk: 'NB', danish: 'DA', dansk: 'DA',
  finnish: 'FI', suomi: 'FI', portuguese: 'PT', portugues: 'PT', 'português': 'PT', russian: 'RU', chinese: 'ZH', japanese: 'JA', korean: 'KO',
  polish: 'PL', polski: 'PL', czech: 'CS', greek: 'EL', turkish: 'TR', arabic: 'AR',
};
/** `Manual_FR`, `manual-de`, `Guide_en-GB`, `Guide_es_ES` — only at the end of the name. */
const LOCALE_SUFFIX = /[_\-\s]([a-z]{2})(?:[_-]([a-z]{2}))?$/i;
const stripExt = (name: string) => name.replace(/\.[a-z0-9]+$/i, '');
const known = (code: string) => (LANG_CODES.has(code.toUpperCase()) ? code.toUpperCase() : null);

function folderLanguage(dir: string): string | null {
  const d = dir.trim().toLowerCase();
  if (LANG_NAMES[d]) return LANG_NAMES[d];
  const m = /^([a-z]{2})(?:[-_]([a-z]{2}))?$/i.exec(d);
  return m ? known(m[1]) : null;
}

export function detectLanguage(name: string, dirs: string[] = []): { language: string; english: boolean; certain: boolean } {
  const found = (lang: string) => ({ language: lang, english: lang === 'EN', certain: true });
  const b = name.match(LOCALE_BRACKET);
  if (b) return found(b[1].toUpperCase());
  const p = name.match(LOCALE_PAREN);
  if (p) return found(p[1].toUpperCase().split('-')[0]);
  const s = stripExt(name).match(LOCALE_SUFFIX);
  const suffix = s && known(s[1]);
  if (suffix) return found(suffix);
  for (const dir of [...dirs].reverse()) { const f = folderLanguage(dir); if (f) return found(f); }
  return { language: 'EN?', english: true, certain: false };   // no token — assume English, flag uncertain
}

export function detectKind(pathSegments: string[], fileName: string): { kind: DocKind; label: string } {
  // Folder names are the strongest signal (B&G: Brand/Product/DocType/file);
  // fall back to the filename itself (Raymarine encodes it there).
  for (const seg of [...pathSegments].reverse()) {
    for (const r of KIND_RULES) if (r.re.test(seg)) return { kind: r.kind, label: r.label };
  }
  for (const r of KIND_RULES) if (r.re.test(fileName)) return { kind: r.kind, label: r.label };
  return { kind: 'other', label: 'Document' };
}

/** Strip locale tokens, revision noise and extension for duplicate-grouping. */
export function normalizedName(name: string): string {
  return displayBase(name).toLowerCase();
}

/** The file name without extension, locale tokens or extra spaces, case kept. */
function displayBase(name: string): string {
  const bare = stripExt(name).replace(LOCALE_BRACKET, '').replace(LOCALE_PAREN, '');
  const s = bare.match(LOCALE_SUFFIX);
  return (s && known(s[1]) ? bare.slice(0, s.index) : bare).replace(/[_\s]+/g, ' ').trim();
}

/** What a file name adds to "Product — Kind": the name minus the product and the kind words. */
function titleExtra(fileName: string, product: string): string {
  let t = displayBase(fileName).replace(/\b(installation guide|installation manual|operation manual|operator'?s? manual|user guide|user manual|quick start guide|manual|guide|instructions)\b/gi, ' ');
  t = t.replace(/\s+/g, ' ').trim();
  if (t.toLowerCase().startsWith(product.toLowerCase())) t = t.slice(product.length);
  return t.replace(/^[\s\-–—·,:]+|[\s\-–—·,:]+$/g, '').slice(0, 80);
}

/** Map a document kind onto the closest catalog type key for the section. */
export function catalogTypeFor(kind: DocKind, catalogTypes: Array<{ key: string; label: string }>): string {
  const want: Record<DocKind, RegExp> = {
    installation: /install/i, operation: /manual|operation|user/i, quickstart: /manual|guide|quick/i,
    service: /service|maintenance|manual/i, compliance: /certificate|compliance|other/i, template: /other/i, other: /manual|other/i,
  };
  const hit = catalogTypes.find((t) => want[kind].test(t.label) || want[kind].test(t.key));
  return hit?.key ?? catalogTypes.find((t) => /other/i.test(t.key))?.key ?? catalogTypes[0]?.key ?? 'other';
}

export interface ClassifyOptions {
  englishOnly: boolean;
  excludeCompliance: boolean;
  catalogTypes: Array<{ key: string; label: string }>;
  /** Content hashes by relative path, where the wizard has them: duplicates are grouped by these. */
  sha?: ReadonlyMap<string, string>;
  /** The user's own include (true) / exclude (false) choices by relative path — they win over the filters. */
  overrides?: ReadonlyMap<string, boolean>;
}

const isDocFile = (rel: string) => !rel.includes('/.listing_cache/') && DOC_EXTS.has((rel.split('.').pop() ?? '').toLowerCase());

/**
 * Files that could be byte-identical to another — the same size is necessary
 * for that — and so are worth hashing before the review, so that duplicates
 * are grouped by content rather than by name.
 */
export function duplicateSuspects(files: ScanFile[]): string[] {
  const bySize = new Map<number, string[]>();
  for (const f of files) {
    const rel = f.relPath.replace(/\\/g, '/');
    if (!isDocFile(rel)) continue;
    const g = bySize.get(f.file.size) ?? [];
    g.push(rel);
    bySize.set(f.file.size, g);
  }
  return [...bySize.values()].filter((g) => g.length > 1).flat();
}

export function classify(files: ScanFile[], opts: ClassifyOptions): { proposals: Proposal[]; stats: ScanStats } {
  let ignoredOther = 0;
  const docs: Array<Omit<Proposal, 'dupOf' | 'mergedProducts' | 'include' | 'excludeReason'>> = [];
  let sidecarCount = 0;

  for (const f of files) {
    const rel = f.relPath.replace(/\\/g, '/');
    if (rel.includes('/.listing_cache/')) { sidecarCount++; continue; }
    const parts = rel.split('/').filter(Boolean);
    const fileName = parts[parts.length - 1] ?? f.file.name;
    const ext = (fileName.split('.').pop() ?? '').toLowerCase();
    if (!DOC_EXTS.has(ext)) { ignoredOther++; continue; }

    const brand = parts.length > 1 ? parts[0] : 'Library';
    const product = (parts.length > 2 ? parts[1] : brand).slice(0, 80);
    const dirs = parts.slice(0, -1);
    const { kind, label } = detectKind(dirs, fileName);
    const lang = detectLanguage(fileName, dirs.slice(1));   // parts[0] is the brand, never a language
    // Title: "Product — Kind"; a generic document keeps its file name. Same-kind
    // documents of one product are told apart after grouping (disambiguate).
    const extra = titleExtra(fileName, product);
    const title = `${product} — ${label}${extra && kind === 'other' ? ` · ${extra}` : ''}`.slice(0, 200);

    docs.push({
      key: rel, file: f.file, relPath: rel, brand, product, kind, kindLabel: label,
      typeKey: catalogTypeFor(kind, opts.catalogTypes), title,
      language: lang.language, english: lang.english, sizeBytes: f.file.size, oversize: f.file.size > MAX_FILE_BYTES,
    });
  }

  // Duplicate grouping: the same content hash ⇒ one upload, several products.
  // Without a hash, identical size + locale-stripped name stands in for it.
  const groups = new Map<string, typeof docs>();
  for (const d of docs) {
    const sha = opts.sha?.get(d.key);
    const gk = sha ? `sha:${sha}` : `${d.sizeBytes}:${normalizedName(d.relPath.split('/').pop() ?? '')}`;
    const g = groups.get(gk) ?? [];
    g.push(d);
    groups.set(gk, g);
  }

  const proposals: Proposal[] = [];
  for (const g of groups.values()) {
    const primary = g.find((d) => d.english) ?? g[0];
    const mergedProducts = Array.from(new Set(g.map((d) => d.product)));
    for (const d of g) {
      const isPrimary = d === primary;
      let excludeReason: Proposal['excludeReason'] = null;
      const choice = opts.overrides?.get(d.key);
      if (d.oversize) excludeReason = 'oversize';
      else if (!isPrimary) excludeReason = 'duplicate';
      else if (choice !== undefined) excludeReason = choice ? null : 'manual';
      else if (opts.englishOnly && !d.english) excludeReason = 'translation';
      else if (opts.excludeCompliance && d.kind === 'compliance') excludeReason = 'compliance';
      proposals.push({
        ...d,
        dupOf: isPrimary ? null : primary.key,
        mergedProducts: isPrimary && mergedProducts.length > 1 ? mergedProducts : [],
        include: excludeReason === null,
        excludeReason,
      });
    }
  }

  disambiguate(proposals);
  proposals.sort((a, b) => a.product.localeCompare(b.product) || a.title.localeCompare(b.title));
  const included = proposals.filter((p) => p.include);
  const count = (f: (p: Proposal) => boolean) => proposals.filter(f).length;
  return {
    proposals,
    stats: {
      totalFiles: files.length, docFiles: docs.length, ignoredOther,
      oversize: count((p) => p.oversize), translations: count((p) => p.excludeReason === 'translation'), duplicates: count((p) => !!p.dupOf),
      products: Array.from(new Set(included.map((p) => p.product))).sort(),
      kinds: Array.from(new Set(included.map((p) => p.kind))),
      sidecarCount,
    },
  };
}

/**
 * No two documents that could be published share a title: first the file
 * name's own words are added ("Zeus3 — Operation · Radar"), then the language,
 * then a number. Duplicates never upload, so they are left alone.
 */
function disambiguate(proposals: Proposal[]): void {
  const live = proposals.filter((p) => !p.dupOf).sort((a, b) => a.relPath.localeCompare(b.relPath));
  const collisions = () => {
    const by = new Map<string, Proposal[]>();
    for (const p of live) { const k = p.title.toLowerCase(); by.set(k, [...(by.get(k) ?? []), p]); }
    return [...by.values()].filter((g) => g.length > 1);
  };
  for (const g of collisions()) {
    for (const p of g) {
      const extra = p.kind === 'other' ? '' : titleExtra(p.relPath.split('/').pop() ?? '', p.product);
      if (extra) p.title = `${p.title} · ${extra}`.slice(0, 200);
    }
  }
  for (const g of collisions()) {
    if (new Set(g.map((p) => p.language)).size > 1) for (const p of g) if (!p.english) p.title = `${p.title} (${p.language})`.slice(0, 200);
  }
  for (const g of collisions()) g.slice(1).forEach((p, i) => { p.title = `${p.title.slice(0, 194)} (${i + 2})`; });
}

export function tagsFor(p: Proposal, batchId: string): string[] {
  const t = [p.brand, ...(p.mergedProducts.length ? p.mergedProducts : [p.product]), p.kindLabel, p.language.replace('?', ''), `import:${batchId}`];
  return Array.from(new Set(t.map((x) => x.trim().toLowerCase().slice(0, 40)).filter(Boolean))).slice(0, 20);
}

export function reportCsv(rows: Array<{ relPath: string; status: string; detail: string }>): string {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  return ['file,status,detail', ...rows.map((r) => `${esc(r.relPath)},${esc(r.status)},${esc(r.detail)}`)].join('\n');
}

/** SHA-256 of a file, lowercase hex — matches the API's content addressing. */
export async function sha256HexOf(file: File): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Recursively collect files from a drag-dropped directory. */
export async function filesFromDataTransfer(items: DataTransferItemList): Promise<ScanFile[]> {
  const out: ScanFile[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
      out.push({ file, relPath: `${prefix}${entry.name}` });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns batches of ≤100 — loop until drained.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const e of batch) await walk(e, `${prefix}${entry.name}/`);
      }
    }
  };
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const e = items[i].webkitGetAsEntry?.();
    if (e) entries.push(e);
  }
  for (const e of entries) await walk(e, '');
  return out;
}
